'use strict';

const flatpickrModule = require('flatpickr');
const flatpickr = flatpickrModule.default || flatpickrModule;

const SELECTOR = 'input.lvz-datepicker';
const DEFAULT_MAX_AGE = 16;
const DEFAULT_YEAR_SPAN = 100;

const LOCALES = {
	it: require('flatpickr/dist/l10n/it.js').Italian,
	fr: require('flatpickr/dist/l10n/fr.js').French,
	de: require('flatpickr/dist/l10n/de.js').German,
	es: require('flatpickr/dist/l10n/es.js').Spanish,
	nl: require('flatpickr/dist/l10n/nl.js').Dutch,
	pt: require('flatpickr/dist/l10n/pt.js').Portuguese,
	pl: require('flatpickr/dist/l10n/pl.js').Polish,
	ro: require('flatpickr/dist/l10n/ro.js').Romanian,
	hu: require('flatpickr/dist/l10n/hu.js').Hungarian,
	cs: require('flatpickr/dist/l10n/cs.js').Czech,
	sv: require('flatpickr/dist/l10n/sv.js').Swedish,
	da: require('flatpickr/dist/l10n/da.js').Danish,
	fi: require('flatpickr/dist/l10n/fi.js').Finnish,
	hr: require('flatpickr/dist/l10n/hr.js').Croatian,
	sk: require('flatpickr/dist/l10n/sk.js').Slovak,
	sl: require('flatpickr/dist/l10n/sl.js').Slovenian,
	bg: require('flatpickr/dist/l10n/bg.js').Bulgarian
};

function getBooleanAttribute(input, attributeName) {
	const raw = input.getAttribute(attributeName);

	if (raw === null) {
		return false;
	}

	return raw === '' || raw === 'true';
}

function getStringAttribute(input, attributeName) {
	const raw = input.getAttribute(attributeName);

	return raw === null || raw === '' ? null : raw;
}

function resolveMinDate(input) {
	const raw = getStringAttribute(input, 'data-lvz-datepicker-min-date');

	if (!raw) {
		return undefined;
	}

	if (raw === 'today') {
		return 'today';
	}

	return raw;
}

function resolveMaxDate(input) {
	let raw = getStringAttribute(input, 'data-lvz-datepicker-max-date');

	if (raw) {
		return raw;
	}

	raw = input.getAttribute('data-lvz-max-age');

	if (raw === null || raw === '') {
		return undefined;
	}

	let years = parseInt(raw, 10);

	if (isNaN(years) || years < 0) {
		years = DEFAULT_MAX_AGE;
	}

	const max = new Date();

	max.setFullYear(max.getFullYear() - years);
	max.setHours(23, 59, 59, 999);

	return max;
}

/**
 * Resolve flatpickr locale from <html lang>.
 * @returns {Object|undefined}
 */
function resolveLocale() {
	const lang = String(document.documentElement.lang || '').toLowerCase().split(/[_-]/)[0];

	return LOCALES[lang] || undefined;
}

/**
 * Year range for the year dropdown.
 * @param {Object} instance - flatpickr instance
 * @param {HTMLInputElement} input
 * @returns {{ minYear: number, maxYear: number }}
 */
function resolveYearRange(instance, input) {
	const nowYear = new Date().getFullYear();
	const maxYear = instance.config.maxDate
		? instance.config.maxDate.getFullYear()
		: nowYear;
	let minYear;

	if (instance.config.minDate) {
		minYear = instance.config.minDate.getFullYear();
	} else {
		const spanRaw = input.getAttribute('data-lvz-year-span');
		let span = parseInt(spanRaw, 10);

		if (isNaN(span) || span < 1) {
			span = DEFAULT_YEAR_SPAN;
		}

		minYear = maxYear - span;
	}

	if (minYear > maxYear) {
		minYear = maxYear;
	}

	return { minYear: minYear, maxYear: maxYear };
}

/**
 * Optional Gigya birth-part sync when those fields exist in the same form.
 * @param {HTMLElement} input
 * @param {Date|undefined} date
 */
function syncBirthParts(input, date) {
	const $form = $(input).closest('form, #gigya-handle');

	if (!$form.length) {
		return;
	}

	const $day = $form.find('input[name="profile.birthDay"]');
	const $month = $form.find('input[name="profile.birthMonth"]');
	const $year = $form.find('input[name="profile.birthYear"]');

	if (!$day.length && !$month.length && !$year.length) {
		return;
	}

	if (!date) {
		$day.val('');
		$month.val('');
		$year.val('');
		return;
	}

	$day.val(String(date.getDate()));
	$month.val(String(date.getMonth() + 1));
	$year.val(String(date.getFullYear()));
}

/**
 * Wrap a select with caret styling hook.
 * @param {HTMLElement} select
 */
function wrapSelectWithCaret(select) {
	if (!select || !select.parentNode) {
		return;
	}

	if ($(select).parent().hasClass('lvz-datepicker-select-wrap')) {
		return;
	}

	// Use <div>: Flatpickr hides/absolutely positions spans inside .numInputWrapper
	$(select).wrap('<div class="lvz-datepicker-select-wrap"></div>');
}

/**
 * Replace Flatpickr year text input with a select dropdown.
 * @param {Object} instance - flatpickr instance
 * @param {HTMLInputElement} input
 */
function enhanceYearSelect(instance, input) {
	const calendar = instance.calendarContainer;
	const currentMonth = calendar.querySelector('.flatpickr-current-month');
	const yearInput = instance.currentYearElement;
	const monthSelect = instance.monthsDropdownContainer
		|| calendar.querySelector('.flatpickr-monthDropdown-months');

	if (!currentMonth) {
		return;
	}

	wrapSelectWithCaret(monthSelect);

	if (currentMonth.querySelector('.flatpickr-yearDropdown-years')) {
		return;
	}

	// Hide native year input wrapper (spinners / text field)
	if (yearInput && yearInput.parentNode) {
		yearInput.parentNode.classList.add('lvz-datepicker-year-native');
		yearInput.parentNode.setAttribute('hidden', 'hidden');
	}

	const range = resolveYearRange(instance, input);
	const select = document.createElement('select');
	let y;

	select.className = 'flatpickr-yearDropdown-years';
	select.setAttribute('aria-label', document.body.getAttribute('data-datepicker-year-label') || '');

	for (y = range.maxYear; y >= range.minYear; y -= 1) {
		const opt = document.createElement('option');

		opt.value = String(y);
		opt.textContent = String(y);

		if (y === instance.currentYear) {
			opt.selected = true;
		}

		select.appendChild(opt);
	}

	select.addEventListener('change', function () {
		instance.changeYear(parseInt(select.value, 10));
	});

	currentMonth.appendChild(select);
	wrapSelectWithCaret(select);

	instance.config.onYearChange.push(function () {
		select.value = String(instance.currentYear);
	});
}

/**
 * Flatpickr always pads a single-month view to 42 cells (6 full weeks) to
 * keep a stable height across months. Hide the trailing week once the
 * preceding rows already cover every day of the current month.
 * @param {Object} instance - flatpickr instance
 */
function trimTrailingWeek(instance) {
	const days = instance.calendarContainer.querySelectorAll('.dayContainer .flatpickr-day');
	let lastCurrentMonthIndex = -1;

	days.forEach(function (day, index) {
		if (!day.classList.contains('prevMonthDay') && !day.classList.contains('nextMonthDay')) {
			lastCurrentMonthIndex = index;
		}
	});

	if (lastCurrentMonthIndex === -1) {
		return;
	}

	const neededCells = Math.ceil((lastCurrentMonthIndex + 1) / 7) * 7;

	days.forEach(function (day, index) {
		if (index >= neededCells) {
			day.setAttribute('hidden', 'hidden');
		} else {
			day.removeAttribute('hidden');
		}
	});
}

/**
 * Replace Flatpickr's separate month dropdown + year input with a single
 * "Month Year" dropdown (design calls for one combined control, not two).
 * @param {Object} instance - flatpickr instance
 */
function enhanceMonthYearSelect(instance) {
	const calendar = instance.calendarContainer;
	const currentMonth = calendar.querySelector('.flatpickr-current-month');
	const monthElement = currentMonth && currentMonth.querySelector('.cur-month');
	const yearElement = instance.currentYearElement;

	if (!currentMonth || currentMonth.querySelector('.lvz-datepicker-monthYear-select')) {
		return;
	}

	if (monthElement) {
		monthElement.setAttribute('hidden', 'hidden');
	}

	if (yearElement && yearElement.parentNode) {
		yearElement.parentNode.classList.add('lvz-datepicker-year-native');
		yearElement.parentNode.setAttribute('hidden', 'hidden');
	}

	// ponytail: falls back to a 100-year-back / 12-month-forward window when no min/maxDate is supplied; widen if a use case needs a longer range
	const start = instance.config.minDate || new Date(instance.now.getFullYear() - 100, instance.now.getMonth(), 1);
	const end = instance.config.maxDate || new Date(start.getFullYear(), start.getMonth() + 12, 1);
	const months = instance.l10n.months.shorthand;
	const select = document.createElement('select');
	let y;

	select.className = 'lvz-datepicker-monthYear-select';
	select.setAttribute('aria-label', document.body.getAttribute('data-datepicker-year-label') || '');

	for (y = start.getFullYear(); y <= end.getFullYear(); y += 1) {
		const mStart = y === start.getFullYear() ? start.getMonth() : 0;
		const mEnd = y === end.getFullYear() ? end.getMonth() : 11;
		let m;

		for (m = mStart; m <= mEnd; m += 1) {
			const opt = document.createElement('option');

			opt.value = y + '-' + m;
			opt.textContent = months[m] + ' ' + y;

			if (y === instance.currentYear && m === instance.currentMonth) {
				opt.selected = true;
			}

			select.appendChild(opt);
		}
	}

	select.addEventListener('change', function () {
		const parts = select.value.split('-');

		instance.jumpToDate(new Date(parseInt(parts[0], 10), parseInt(parts[1], 10), 1));
	});

	currentMonth.insertBefore(select, currentMonth.firstChild);
	wrapSelectWithCaret(select);

	function syncSelect() {
		select.value = instance.currentYear + '-' + instance.currentMonth;
	}

	instance.config.onMonthChange.push(syncSelect);
	instance.config.onYearChange.push(syncSelect);
}

/**
 * Replace Flatpickr's month/year header with a "Mon YYYY" toggle button that
 * swaps the day grid for a year-grid, then a month-grid (year first, then
 * month, then days — instead of a flat combined dropdown).
 * @param {Object} instance - flatpickr instance
 * @param {HTMLInputElement} input
 */
function enhanceYearGridToggle(instance, input) {
	const calendar = instance.calendarContainer;
	const currentMonth = calendar.querySelector('.flatpickr-current-month');
	const monthElement = currentMonth && currentMonth.querySelector('.cur-month');
	const yearElement = instance.currentYearElement;
	const innerContainer = calendar.querySelector('.flatpickr-innerContainer');

	if (!currentMonth || currentMonth.querySelector('.lvz-datepicker-year-toggle')) {
		return;
	}

	if (monthElement) {
		monthElement.setAttribute('hidden', 'hidden');
	}

	if (yearElement && yearElement.parentNode) {
		yearElement.parentNode.classList.add('lvz-datepicker-year-native');
		yearElement.parentNode.setAttribute('hidden', 'hidden');
	}

	const yearLabel = document.body.getAttribute('data-datepicker-year-label') || '';

	const toggle = document.createElement('button');
	toggle.type = 'button';
	toggle.className = 'lvz-datepicker-year-toggle';
	toggle.setAttribute('aria-expanded', 'false');
	toggle.setAttribute('aria-label', yearLabel);

	const label = document.createElement('span');
	label.className = 'lvz-datepicker-year-toggle__label';
	toggle.appendChild(label);

	const caret = document.createElement('span');
	caret.className = 'lvz-datepicker-year-toggle__caret';
	caret.setAttribute('aria-hidden', 'true');
	toggle.appendChild(caret);

	currentMonth.insertBefore(toggle, currentMonth.firstChild);

	const yearGrid = document.createElement('div');
	yearGrid.className = 'lvz-datepicker-year-grid';
	yearGrid.setAttribute('hidden', 'hidden');
	calendar.insertBefore(yearGrid, innerContainer);

	const monthGrid = document.createElement('div');
	monthGrid.className = 'lvz-datepicker-month-grid';
	monthGrid.setAttribute('hidden', 'hidden');
	calendar.insertBefore(monthGrid, innerContainer);

	function syncLabel() {
		label.textContent = instance.l10n.months.shorthand[instance.currentMonth] + ' ' + instance.currentYear;
	}

	function isPanelOpen() {
		return calendar.classList.contains('lvz-datepicker-calendar--year-view')
			|| calendar.classList.contains('lvz-datepicker-calendar--month-view');
	}

	// Center the active button within its own scrollable grid only. Element.scrollIntoView()
	// walks up to any scrollable ancestor (including the page), which visibly scrolled the
	// whole viewport when a year/month was picked — this stays scoped to `container.scrollTop`.
	function scrollActiveIntoView(container, active) {
		if (!active) {
			return;
		}

		container.scrollTop = Math.max(0, active.offsetTop - (container.clientHeight / 2) + (active.clientHeight / 2));
	}

	function closeToDayView() {
		calendar.classList.remove('lvz-datepicker-calendar--year-view', 'lvz-datepicker-calendar--month-view');
		toggle.setAttribute('aria-expanded', 'false');
		yearGrid.setAttribute('hidden', 'hidden');
		monthGrid.setAttribute('hidden', 'hidden');
	}

	function renderMonthButtons() {
		const months = instance.l10n.months.shorthand;
		let m;

		monthGrid.innerHTML = '';

		for (m = 0; m <= 11; m += 1) {
			const btn = document.createElement('button');

			btn.type = 'button';
			btn.className = 'lvz-datepicker-month-grid__month';
			btn.textContent = months[m];

			if (m === instance.currentMonth) {
				btn.classList.add('lvz-datepicker-month-grid__month--active');
			}

			btn.addEventListener('click', (function (month) {
				return function () {
					instance.jumpToDate(new Date(instance.currentYear, month, 1), true);
					closeToDayView();
				};
			}(m)));

			monthGrid.appendChild(btn);
		}
	}

	function openMonthView() {
		renderMonthButtons();
		calendar.classList.remove('lvz-datepicker-calendar--year-view');
		calendar.classList.add('lvz-datepicker-calendar--month-view');
		toggle.setAttribute('aria-expanded', 'true');
		yearGrid.setAttribute('hidden', 'hidden');
		monthGrid.removeAttribute('hidden');

		scrollActiveIntoView(monthGrid, monthGrid.querySelector('.lvz-datepicker-month-grid__month--active'));
	}

	function renderYearButtons() {
		const range = resolveYearRange(instance, input);
		let y;

		yearGrid.innerHTML = '';

		for (y = range.maxYear; y >= range.minYear; y -= 1) {
			const btn = document.createElement('button');

			btn.type = 'button';
			btn.className = 'lvz-datepicker-year-grid__year';
			btn.textContent = String(y);

			if (y === instance.currentYear) {
				btn.classList.add('lvz-datepicker-year-grid__year--active');
			}

			btn.addEventListener('click', (function (year) {
				return function () {
					instance.changeYear(year);
					openMonthView();
				};
			}(y)));

			yearGrid.appendChild(btn);
		}
	}

	function openYearView() {
		renderYearButtons();
		calendar.classList.remove('lvz-datepicker-calendar--month-view');
		calendar.classList.add('lvz-datepicker-calendar--year-view');
		toggle.setAttribute('aria-expanded', 'true');
		monthGrid.setAttribute('hidden', 'hidden');
		yearGrid.removeAttribute('hidden');

		scrollActiveIntoView(yearGrid, yearGrid.querySelector('.lvz-datepicker-year-grid__year--active'));
	}

	toggle.addEventListener('click', function () {
		if (isPanelOpen()) {
			closeToDayView();
		} else {
			openYearView();
		}
	});

	syncLabel();
	instance.config.onMonthChange.push(syncLabel);
	instance.config.onYearChange.push(syncLabel);
}

/**
 * Prepare input markup for Flatpickr + icon.
 * @param {HTMLInputElement} input
 */
function prepareInput(input) {
	const $input = $(input);

	if (input.getAttribute('type') === 'date') {
		input.setAttribute('type', 'text');
	}

	input.setAttribute('autocomplete', 'off');
	input.classList.add('lvz-datepicker');

	if (!$input.parent().hasClass('lvz-datepicker-field')) {
		$input.wrap('<div class="lvz-datepicker-field"></div>');
		$input.after('<span class="lvz-datepicker-icon" aria-hidden="true"></span>');
	}
}

/**
 * Match popup width to the rendered date input when requested.
 * @param {Object} instance
 * @param {HTMLInputElement} input
 */
function syncCalendarWidth(instance, input) {
	if (!instance || !instance.calendarContainer || !getBooleanAttribute(input, 'data-lvz-datepicker-match-input-width')) {
		return;
	}

	const field = input.closest('.lvz-datepicker-field') || input;
	const width = field && field.getBoundingClientRect ? field.getBoundingClientRect().width : 0;
	if (width > 0) {
		instance.calendarContainer.style.width = Math.round(width) + 'px';
	}
}

/**
 * Init all `.lvz-datepicker` inputs inside context.
 * @param {jQuery|HTMLElement|Document} [$context]
 */
function refresh($context) {
	const $root = $context && $context.jquery ? $context : $($context || document);
	const locale = resolveLocale();

	$root.find(SELECTOR).each(function () {
		const input = this;

		if (input._flatpickr) {
			return;
		}

		prepareInput(input);

		const combinedMonthYear = getBooleanAttribute(input, 'data-lvz-datepicker-combined-month-year');
		const yearGridToggle = getBooleanAttribute(input, 'data-lvz-datepicker-year-grid');
		const trimTrailingWeekFlag = getBooleanAttribute(input, 'data-lvz-datepicker-trim-trailing-week');
		const enhancedGrid = getBooleanAttribute(input, 'data-lvz-datepicker-enhanced-grid');

		const options = {
			dateFormat: 'Y-m-d',
			allowInput: false,
			disableMobile: true,
			shorthandCurrentMonth: true,
			locale: Object.assign({}, locale || {}, { firstDayOfWeek: 1 }),
			monthSelectorType: (combinedMonthYear || yearGridToggle) ? 'static' : 'dropdown',
			onReady: function (selectedDates, dateStr, instance) {
				instance.calendarContainer.classList.add('lvz-datepicker-calendar');

				if (enhancedGrid) {
					instance.calendarContainer.classList.add('lvz-datepicker-calendar--enhanced-grid');
				}

				if (yearGridToggle) {
					enhanceYearGridToggle(instance, input);
				} else if (combinedMonthYear) {
					enhanceMonthYearSelect(instance);
				} else {
					enhanceYearSelect(instance, input);
				}

				if (trimTrailingWeekFlag && instance.daysContainer) {
					trimTrailingWeek(instance);

					// instance.redraw() (used elsewhere to refresh the grid on drawer
					// open) rebuilds days without firing onMonthChange/onYearChange.
					// Observe the stable wrapper instead so the trim re-applies after
					// any rebuild, not just real month navigation.
					const daysObserver = new MutationObserver(function () {
						trimTrailingWeek(instance);
					});

					daysObserver.observe(instance.daysContainer, { childList: true });
				}

				syncCalendarWidth(instance, input);

				if (selectedDates[0]) {
					syncBirthParts(input, selectedDates[0]);
				}
			},
			onOpen: function (selectedDates, dateStr, instance) {
				syncCalendarWidth(instance, input);
			},
			onChange: function (selectedDates) {
				syncBirthParts(input, selectedDates[0]);
				$(input).trigger('input').trigger('change');
			}
		};
		const customDateFormat = getStringAttribute(input, 'data-lvz-datepicker-date-format');
		const defaultDate = getStringAttribute(input, 'data-lvz-datepicker-default-date');
		const inline = getBooleanAttribute(input, 'data-lvz-datepicker-inline');
		const allowInvalidPreload = getBooleanAttribute(input, 'data-lvz-datepicker-allow-invalid-preload');
		const minDate = resolveMinDate(input);

		const maxDate = resolveMaxDate(input);

		if (customDateFormat) {
			options.dateFormat = customDateFormat;
		}

		if (defaultDate) {
			options.defaultDate = defaultDate;
		}

		if (inline) {
			options.inline = true;
			options.clickOpens = false;
		}

		if (allowInvalidPreload) {
			options.allowInvalidPreload = true;
		}

		if (minDate) {
			options.minDate = minDate;
		}

		if (maxDate) {
			options.maxDate = maxDate;
		}

		flatpickr(input, options);
	});
}

module.exports = function () {
	refresh();

	$(document).on('update:lvz-datepicker', function (e, data) {
		refresh(data && data.$context);
	});
};
